import React, { useState } from 'react';
import { Avatar, Chip } from '@heroui/react';
import { Typography } from '../../../../components';
import { ResponsiveTooltip } from '../../../../components/ui';
import { UserGroup2, UserNoAvatarIcon } from '../../../../components/icons';
import { ProposalData } from '../types';
import { formatRating } from '../utils';
import { useTranslations } from '../../../../contexts/translation.context';
import PartnerDetailsModal from './PartnerDetailsModal';

interface PartnerCellProps {
  proposal: ProposalData;
}

const PartnerCell: React.FC<PartnerCellProps> = ({ proposal }) => {
  const { t } = useTranslations();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Function to get correct plural forms with i18n
  const getPluralForm = (count: number, type: 'orders' | 'active') => {
    // For Russian, check plurals
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (type === 'orders') {
      if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return t('proposals.partner.ordersMany');
      } else if (lastDigit === 1) {
        return t('proposals.partner.orders');
      } else if (lastDigit >= 2 && lastDigit <= 4) {
        return t('proposals.partner.ordersPlural');
      } else {
        return t('proposals.partner.ordersMany');
      }
    } else {
      if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return t('proposals.partner.activePlural');
      } else if (lastDigit === 1) {
        return t('proposals.partner.active');
      } else {
        return t('proposals.partner.activePlural');
      }
    }
  };

  // Determine partner level chip style
  const getPartnerLevelStyle = (rating: any) => {
    console.log('rating', rating);
    if (rating === 'new')
      return {
        bg: 'bg-success/20',
        text: 'text-success',
        label: t('proposals.partnerLevels.new'),
      };
    if (rating === 'base')
      return {
        bg: 'bg-[#EEEEEF]',
        text: 'text-auth-muted',
        label: t('proposals.partnerLevels.base'),
      };
    if (rating === 'pro')
      return {
        bg: 'bg-auth-ink',
        text: 'text-white',
        label: t('proposals.partnerLevels.pro'),
      };
    if (rating === 'expert')
      return {
        bg: 'bg-accent/20',
        text: 'text-accent',
        label: t('proposals.partnerLevels.expert'),
      };
    return {
      bg: 'bg-[#EEEEEF]',
      text: 'text-auth-muted',
      label: t('proposals.partnerLevels.base'),
    };
  };

  const partnerLevel = getPartnerLevelStyle(proposal.partner.lvl);

  return (
    <>
      <div className="flex items-start gap-3">
        <div className="relative">
          <Avatar
            src={proposal.partner.avatar}
            showFallback
            name={proposal.partner.name?.slice(0, 1)}
            className="w-10 h-10 rounded-3"
          />
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-1 mb-1">
            <ResponsiveTooltip
              content={t('proposals.partner.nameTooltip')}
              placement="top"
            >
              <span
                onClick={() => setIsModalOpen(true)}
                className="text-sm font-medium text-partner cursor-pointer hover:underline"
              >
                {proposal.partner.name}
              </span>
            </ResponsiveTooltip>
          </div>
          <div className="flex flex-wrap gap-1 mb-1">
            <ResponsiveTooltip
              content={t('proposals.partner.levelTooltip')}
              placement="top"
            >
              <span
                className={`px-2 py-1 rounded-full !h-6 text-xs font-regular ${partnerLevel.bg} ${partnerLevel.text}`}
              >
                {partnerLevel.label}
              </span>
            </ResponsiveTooltip>
            <ResponsiveTooltip
              content={t('proposals.partner.platformTooltip')}
              placement="top"
            >
              <div className="flex items-center gap-1 px-2 py-1 border !h-6 border-[1.4px] border-auth-strength-none rounded-xl">
                {proposal.partner.platformIcon}
                <span className="text-xs text-auth-ink">{proposal.partner.platform}</span>
                {proposal?.of_models !== undefined && proposal.of_models > 0 && (
                  <ResponsiveTooltip
                    content={t('proposals.partner.accountsInOfferTooltip')}
                    placement="top"
                  >
                    <div className="flex items-center gap-0 cursor-pointer">
                      <div className="w-[1px] h-3 bg-auth-strength-none mx-1"></div>
                      <UserGroup2 />
                      <Typography variant="body3">{proposal?.of_models}</Typography>
                    </div>
                  </ResponsiveTooltip>
                )}
              </div>
            </ResponsiveTooltip>
          </div>
          <ResponsiveTooltip
            content={t('proposals.partner.statsTooltip')}
            placement="top"
          >
            <div className="flex flex-col gap-1 cursor-pointer">
              <div className="flex items-center gap-1.5 text-xs max-md:whitespace-nowrap">
                <span className="text-auth-muted">
                  {proposal.partner.completedOrders}{' '}
                  {getPluralForm(proposal.partner.completedOrders, 'orders')}
                </span>
                <div className="w-[0.1875rem] h-[0.1875rem] rounded-full bg-auth-strength-none"></div>
                <span className="text-partner">
                  {proposal.statistics.activeOrders}{' '}
                  {getPluralForm(proposal.statistics.activeOrders, 'active')}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs max-md:whitespace-nowrap">
                <span className="text-auth-muted">{t('proposals.partner.completed')}</span>
                <div className="w-[0.1875rem] h-[0.1875rem] rounded-full bg-auth-strength-none"></div>
                <span className="text-partner">{proposal.statistics.successRate}%</span>
              </div>
            </div>
          </ResponsiveTooltip>
        </div>
      </div>

      <PartnerDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        proposal={proposal}
      />
    </>
  );
};

export default PartnerCell;
